/* eslint-disable */
/**
 * Self-Service module registry.
 *
 * Exposes a Vue-reactive `enabledMap` (proxy) so components that read
 * `moduleRegistry.enabledMap[slug]` will re-render automatically when
 * loadModules() resolves. The map is cached in sessionStorage for 5min so
 * back-navigation doesn't hit the API each time.
 *
 * Fail-open: until the first load completes, every module is treated as
 * enabled (the map is empty and `isModuleEnabled` returns true).
 */
import axios from "axios";
import { reactive } from "vue";
import { authUrl } from "@/api";

const CACHE_KEY = "ssModulesMap";
const TTL_MS    = 5 * 60 * 1000;

export const enabledMap = reactive({});
let loaded  = false;
let inflight = null;

function readCache() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.map || !parsed.ts) return null;
    if (Date.now() - parsed.ts > TTL_MS) return null;
    return parsed.map;
  } catch (e) { return null; }
}

function writeCache(map) {
  try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ map, ts: Date.now() })); } catch (e) {}
}

function applyMap(map) {
  // Clear then reassign so reactivity fires for every key.
  for (const k of Object.keys(enabledMap)) delete enabledMap[k];
  if (map && typeof map === "object") {
    for (const k of Object.keys(map)) enabledMap[k] = map[k] !== false;
  }
  loaded = true;
}

function fetchFromBackend() {
  const data = new FormData();
  data.append("function", "getSelfServiceModules");
  return axios.post(authUrl, data)
    .then((res) => {
      if (res && res.data && res.data.success && res.data.data && res.data.data.enabledMap) {
        return res.data.data.enabledMap;
      }
      return {};
    })
    .catch(() => ({}));
}

export function loadModules(force = false) {
  if (!force) {
    const cached = readCache();
    if (cached) { applyMap(cached); return Promise.resolve(cached); }
  }
  if (inflight) return inflight;
  inflight = fetchFromBackend().then((map) => {
    applyMap(map);
    writeCache(map);
    inflight = null;
    return map;
  });
  return inflight;
}

/**
 * Synchronous check — fail-open until the registry has loaded once.
 * Callers wanting reactivity should read `enabledMap[slug]` directly
 * in a template or computed.
 */
export function isModuleEnabled(slug) {
  if (!slug) return true;
  if (!loaded) return true;
  return enabledMap[slug] !== false;
}

/**
 * Path → module-slug lookup. Kept here so the router guard and any nav /
 * services list stay in lockstep.
 */
export const PATH_TO_MODULE = [
  { rx: /^\/(park-fees|park-ticket)/,                                                           slug: "park" },
  { rx: /^\/(daily-parking|seasonal-parking)/,                                                  slug: "parking" },
  { rx: /^\/(create-business|sbp-payment|print-permit|permit)/,                                 slug: "sbp" },
  { rx: /^\/(land-rate|land-rates|land-search|demand-notice)/,                                  slug: "landrate" },
  { rx: /^\/(house-rents|market-stalls)/,                                                       slug: "rent" },
  { rx: /^\/(pay-bill|bill|print-invoice|payment|print-receipt|receipt)/,                       slug: "pay-bill" },
  { rx: /^\/(building-permits|application-acknowledgment|track-application)/,                   slug: "building" },
  { rx: /^\/(health-permit|health-certificates|health\/print-food-hygiene-licence)/,            slug: "health" },
  { rx: /^\/(fire-license|fire-certificate|print-fire-license|print-fire-certificate)/,         slug: "fire" },
  { rx: /^\/(liquor-license|liquor-certificate|track-liquor-application|print-liquor-license)/, slug: "liquor" },
  { rx: /^\/(medical-certificate|track-medical-application|print-medical-certificate)/,         slug: "medical" },
  { rx: /^\/verify-school/,                                                                     slug: "education" },
  { rx: /^\/verify-advert/,                                                                     slug: "advertising" },
  { rx: /^\/verify-premise/,                                                                    slug: "occupational" },
  { rx: /^\/library/,                                                                           slug: "library" },
  { rx: /^\/property-/,                                                                         slug: "valuation" }
];

export function slugForPath(path) {
  if (!path) return null;
  for (const m of PATH_TO_MODULE) if (m.rx.test(path)) return m.slug;
  return null;
}

export default { enabledMap, loadModules, isModuleEnabled, slugForPath, PATH_TO_MODULE };
